import { AfterViewInit, Component, OnDestroy, ViewChild } from '@angular/core';
import { ContextMenuOption } from '../../../models/context-menu-option';
import { DrRequestInfo } from '../../../models/dr-request-info';
import { Endpoint, HelpIndex } from '../../../models/enums';
import { AlignDirection, CellTemplateType, FilterTemplateType, GenericColDef, GridContext } from '../../../models/grid-context';
import { LoggedUser } from '../../../models/logged-user';
import { CommonService } from '../../../services/common/common.service';
import { RestService } from '../../../services/rest/rest.service';
import { interval, Subscription } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { GenericGridComponent } from '../../../components/generic-grid/generic-grid.component';
import { LogoutService } from '../../../services/logout/logout.service';
import { User } from '../../../models/user';
import { MessageType } from '../../../models/enums';
import { ObjectActionListType, ActionType, Permissions } from '../../../models/permission';

@Component({
    standalone: false,
    selector: 'res-active-user-sessions',
    templateUrl: './active-user-sessions.component.html',
    styleUrls: ['./active-user-sessions.component.scss']
})
export class ActiveUserSessionsComponent implements AfterViewInit, OnDestroy {
    @ViewChild(GenericGridComponent, { static: true }) activeSessionsGrid: GenericGridComponent;
    modalHelpIndex: HelpIndex;
    automationId = 'active-user-sessions';
    gridName = 'Active User Sessions';
    requestInfo = new DrRequestInfo({ endpoint: Endpoint.realmManager_listLoggedInUsers, responseName: 'loggedInUsers', data: { contextHandle: this.commonService.currentUser?.originalCustomerName === 'super_system_customer' || this.commonService.currentUser?.importedFromSystemUser ? 'super_system_customer' : '' } });
    contextMenuOptions = [new ContextMenuOption({
        name: 'Log out on All Devices', iconClass: 'fa-sign-out', iconColor: 'color-grey-10', automationId: this.automationId + '-log-out-all-devices-user',
        click: (item) => this.logOutUserOnAllDevices(item),
        enabled: (item) => !item.isCurrentUser,
        tooltipFunction: (item) => item.isCurrentUser ? '' : 'The user will be logged out on all devices and the "Remember Me" password cleared.',
        permissions: [{ permissionType: ObjectActionListType.ACTIVE_USER_SESSIONS, permissionActions: [ActionType.CREATE] }]
    })];
    getActiveSessionsPoller = null;
    private logoutEvent: Subscription;
    isGridBusy = false;
    isGridBusyMessage = 'Loading Active User Sessions ...';
    columnDefs = new Array<GenericColDef>(
        {
            headerName: 'Username',
            automationId: this.automationId,
            lockVisible: true,
            field: 'handle',
            minWidth: 30,
            filterType: FilterTemplateType.freeform,
            sortBy: 'handle',
            cellTemplateType: CellTemplateType.basicLabelWithTooltip
        },
        {
            headerName: 'First Name',
            automationId: this.automationId,
            field: 'firstName',
            minWidth: 30,
            filterType: FilterTemplateType.freeform,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip
        },
        {
            headerName: 'Last Name',
            automationId: this.automationId,
            field: 'lastName',
            minWidth: 30,
            filterType: FilterTemplateType.freeform,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip
        },
        {
            headerName: 'Email',
            automationId: this.automationId,
            field: 'email',
            minWidth: 30,
            filterType: FilterTemplateType.freeform,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip
        },
        {
            headerName: 'Project Name',
            automationId: this.automationId,
            field: 'projectName',
            minWidth: 30,
            filterType: FilterTemplateType.freeform,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip
        },
        {
            headerName: 'Project Description',
            automationId: this.automationId,
            field: 'projectDescription',
            minWidth: 30,
            filterType: FilterTemplateType.freeform,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip,
        },
        {
            headerName: 'Organization',
            automationId: this.automationId,
            minWidth: 30,
            field: 'orgName',
            filterType: FilterTemplateType.freeform,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip
        },
        {
            headerName: 'Session Time',
            automationId: this.automationId,
            minWidth: 30,
            width: 105,
            cellAlignDirection: AlignDirection.right,
            sortBy: 'sessionDuration',
            defaultSortAscending: false,
            field: 'sessionDuration',
            cellTemplateType: CellTemplateType.computedDuration
        },
        {
            headerName: 'Idle Time',
            automationId: this.automationId,
            minWidth: 30,
            width: 90,
            cellAlignDirection: AlignDirection.right,
            field: 'idleDuration',
            sortBy: 'idleDuration',
            defaultSortAscending: false,
            cellTemplateType: CellTemplateType.computedDuration
        },
        {
            headerName: 'IP Address',
            automationId: this.automationId,
            minWidth: 30,
            width: 120,
            field: 'userIpAddress',
            cellTemplateType: CellTemplateType.basicLabelWithTooltip
        },
        {
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.contextMenu,
            minWidth: 30, maxWidth: 30, width: 30,
            suppressSizeToFit: true,
        });

    gridContext = new GridContext({
        sortBy: 'handle',
        sortAscending: true,
        uiMultiFilter: true,
        loadAndFilter: true
    });
    hasCreatePermissions: boolean;
    hasContextMenuPermissions = true;

    constructor(private commonService: CommonService, private restService: RestService, private logoutService: LogoutService) {
        this.logoutEvent = this.commonService.getMessage(MessageType.stopPollingOnActiveUserSessions).subscribe(() => {
            this.stopActiveSessionsPoller();
        });
        this.hasCreatePermissions = Permissions.checkUserPermission(ObjectActionListType.ACTIVE_USER_SESSIONS, ActionType.CREATE);
        if (this.columnDefs.find(c => c.cellTemplateType === CellTemplateType.contextMenu)) {
            if (!this.hasCreatePermissions || this.commonService.isTPAuthEnabledSystem) {
                this.columnDefs = this.columnDefs.filter(col => col.cellTemplateType !== CellTemplateType.contextMenu);
                this.hasContextMenuPermissions = false;
            }
        }
    }

    ngAfterViewInit(): void {
        if (!this.getActiveSessionsPoller) {
            this.startActiveSessionsPoller();
        }
    }

    responseCallback = (response: any) => {
        if (response?.loggedInUsers?.length > 0) {
            response.loggedInUsers = (response.loggedInUsers as Array<any>).map(u => new LoggedUser(u));
            response.loggedInUsers.forEach(loggedUser => {
                loggedUser.isCurrentUser = loggedUser.deviceID === this.commonService.currentUser.deviceId;
            });
            this.activeSessionsGrid.refreshColumns();
        }
    }

    startActiveSessionsPoller(): void {
        const pollInterval = 30000; // 30 sec
        this.getActiveSessionsPoller = interval(pollInterval).pipe(startWith(0)
        ).subscribe(() => this.loadGridData());
    }

    stopActiveSessionsPoller(): void {
        if (this.getActiveSessionsPoller) {
            this.getActiveSessionsPoller.unsubscribe();
            this.getActiveSessionsPoller = null;
        }
    }

    loadGridData(): void {
        if (this.activeSessionsGrid) {
            this.commonService.suppressGridSpinnerIfPoller = true;
            this.activeSessionsGrid.loadData();
        }
    }

    logOutUserOnAllDevices(selectedUser: LoggedUser): void {
        this.commonService.openConfirmationModal(`${selectedUser.handle} will be logged out on all devices.`, 'Confirmation', 'Continue', 'Cancel').then((result) => {
            if (result?.modalResult && selectedUser) {
                this.logoutService.logoutOnAllDevices(new User({ userName: selectedUser.handle, customerName: selectedUser.orgName }));
            }
        });
    }

    openHelp = (): void => this.commonService.openHelp(HelpIndex.sys_user_active_sessions_dlg);

    ngOnDestroy(): void {
        this.stopActiveSessionsPoller();
        this.logoutEvent.unsubscribe();
    }
}
